import React, { useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import notificationService from '../services/notificationService';
import { toast } from 'react-toastify';
import './BookingModal.css';

const BookingModal = ({ teacher, currentUser, userProfile, onClose }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [lessonType, setLessonType] = useState('trial'); // 'trial' or 'regular'
  const [duration, setDuration] = useState('60'); // minutes
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  // Bugünden itibaren 30 gün için tarihler oluştur
  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      // Sadece hafta içi günleri ekle (opsiyonel)
      // if (date.getDay() !== 0 && date.getDay() !== 6) {
      dates.push(date);
      // }
    }
    
    return dates;
  };

  // Saat seçenekleri (09:00 - 20:00 arası)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour <= 20; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      if (hour < 20) {
        slots.push(`${hour.toString().padStart(2, '0')}:30`);
      }
    }
    return slots;
  };

  const availableDates = generateAvailableDates();
  const timeSlots = generateTimeSlots();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedDate || !selectedTime) {
      toast.warning('Please select both date and time');
      return;
    }

    setLoading(true);

    try {
      // Tarih ve saati birleştir
      const [year, month, day] = selectedDate.split('-');
      const [hour, minute] = selectedTime.split(':');
      const bookingDateTime = new Date(year, month - 1, day, hour, minute);

      // Fiyat hesapla
      const price = lessonType === 'trial' 
        ? (teacher.hourlyRate * 0.5) 
        : (teacher.hourlyRate * (parseInt(duration) / 60));

      // Rezervasyon oluştur
      const bookingData = {
        teacherId: teacher.uid || teacher.id,
        teacherName: `${teacher.firstName} ${teacher.lastName}`,
        studentId: currentUser.uid,
        studentName: userProfile.firstName + ' ' + userProfile.lastName,
        studentEmail: currentUser.email,
        
        date: selectedDate,
        time: selectedTime,
        dateTime: bookingDateTime.toISOString(),
        
        lessonType,
        duration: parseInt(duration),
        price: parseFloat(price.toFixed(2)),
        
        notes: notes || '',
        
        status: 'pending', // pending, confirmed, completed, cancelled
        paymentStatus: 'pending', // pending, paid, refunded
        
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Firebase'e kaydet
      const docRef = await addDoc(collection(db, 'bookings'), bookingData);
      
      console.log('✅ Booking created:', docRef.id);
      
      // Öğretmene bildirim gönder
      try {
        await notificationService.notifyBookingRequest(
          teacher.uid || teacher.id,
          {
            id: docRef.id,
            studentName: bookingData.studentName,
            lessonType: bookingData.lessonType,
            dateTime: bookingData.dateTime
          }
        );
        console.log('✅ Notification sent to teacher');
      } catch (notifError) {
        console.error('Notification error:', notifError);
        // Bildirim hatası rezervasyonu engellemez
      }
      
      toast.success('Lesson booked successfully! 🎉');
      onClose();
      
      // MyLessons sayfasına yönlendir (opsiyonel)
      // navigate('/my-lessons');
      
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to book lesson. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    const options = { weekday: 'short', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
  };

  const calculatePrice = () => {
    if (lessonType === 'trial') {
      return (teacher.hourlyRate * 0.5).toFixed(2);
    }
    return (teacher.hourlyRate * (parseInt(duration) / 60)).toFixed(2);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content booking-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div>
            <h2>Book a Lesson</h2>
            <p className="modal-subtitle">
              with {teacher.firstName} {teacher.lastName}
            </p>
          </div>
          <button onClick={onClose} className="modal-close">
            ×
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="modal-body">
          {/* Lesson Type */}
          <div className="form-group">
            <label className="form-label">Lesson Type</label>
            <div className="lesson-type-toggle">
              <button
                type="button"
                className={`toggle-btn ${lessonType === 'trial' ? 'active' : ''}`}
                onClick={() => setLessonType('trial')}
              >
                <div className="toggle-title">Trial Lesson</div>
                <div className="toggle-price">£{(teacher.hourlyRate * 0.5).toFixed(2)}</div>
                <div className="toggle-subtitle">30 minutes</div>
              </button>
              <button
                type="button"
                className={`toggle-btn ${lessonType === 'regular' ? 'active' : ''}`}
                onClick={() => setLessonType('regular')}
              >
                <div className="toggle-title">Regular Lesson</div>
                <div className="toggle-price">£{teacher.hourlyRate}/hr</div>
                <div className="toggle-subtitle">Customizable</div>
              </button>
            </div>
          </div>

          {/* Duration (only for regular lessons) */}
          {lessonType === 'regular' && (
            <div className="form-group">
              <label className="form-label">Duration</label>
              <select 
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="form-select"
              >
                <option value="30">30 minutes</option>
                <option value="60">60 minutes</option>
                <option value="90">90 minutes</option>
                <option value="120">120 minutes</option>
              </select>
            </div>
          )}

          {/* Date Selection */}
          <div className="form-group">
            <label className="form-label">Select Date</label>
            <select
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="form-select"
              required
            >
              <option value="">Choose a date...</option>
              {availableDates.map((date, idx) => (
                <option 
                  key={idx} 
                  value={date.toISOString().split('T')[0]}
                >
                  {formatDate(date)}
                </option>
              ))}
            </select>
          </div>

          {/* Time Selection */}
          <div className="form-group">
            <label className="form-label">Select Time</label>
            <div className="time-slots">
              {timeSlots.map((time) => (
                <button
                  key={time}
                  type="button"
                  className={`time-slot ${selectedTime === time ? 'active' : ''}`}
                  onClick={() => setSelectedTime(time)}
                >
                  {time}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="form-group">
            <label className="form-label">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any specific topics or goals for the lesson?"
              className="form-textarea"
              rows="3"
            />
          </div>

          {/* Price Summary */}
          <div className="price-summary">
            <div className="summary-row">
              <span>Lesson Type:</span>
              <span className="summary-value">
                {lessonType === 'trial' ? 'Trial (30 min)' : `Regular (${duration} min)`}
              </span>
            </div>
            {selectedDate && selectedTime && (
              <div className="summary-row">
                <span>Date & Time:</span>
                <span className="summary-value">
                  {formatDate(new Date(selectedDate))} at {selectedTime}
                </span>
              </div>
            )}
            <div className="summary-row total">
              <span>Total Price:</span>
              <span className="summary-value price-value">£{calculatePrice()}</span>
            </div>
          </div>

          {/* Buttons */}
          <div className="modal-footer">
            <button 
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="btn btn-primary"
              disabled={loading || !selectedDate || !selectedTime}
            >
              {loading ? 'Booking...' : 'Confirm Booking'}
            </button>
          </div>

          {/* Info */}
          <div className="booking-info-box">
            <p>✓ You can cancel or reschedule up to 24 hours before the lesson</p>
            <p>✓ Payment will be processed after the teacher confirms</p>
            <p>✓ You'll receive a confirmation email with meeting details</p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingModal;